/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Persist Biomolecular-Lab structures without blowing Firestore's 1 MiB
 * per-document limit. RCSB-fetched structures are dropped on save and re-fetched
 * from their `url` on open (see stripProjectStateForSave). BUILT / FOLDED /
 * MUTATED structures have no url — their PDB text is the only copy — and a single
 * ESMFold model is ~200-300 KB of text; a handful of them (easy to accumulate
 * now that the agent and the mutate workflow generate them) would push the doc
 * past the limit and the write would be rejected outright.
 *
 * PDB text gzip-deflates ~5-7×, so — exactly as the chat log does
 * (chatCompression.ts) — we compress the no-url structures' data into one
 * Firestore `Bytes` field instead of storing it inline. The doc's `biomolecules`
 * array keeps only the lightweight metadata (name, bioParentName, style, …); the
 * coordinates ride in the compressed sidecar and are re-attached by name on load.
 * Legacy docs (saved before this) carry inline `data` and no sidecar — the load
 * path leaves their inline data untouched.
 */

import { Bytes } from 'firebase/firestore';
import { gzipSync, gunzipSync, strToU8, strFromU8 } from 'fflate';

// Firestore field name for the gzip-compressed {name → PDB} map of the no-url
// structures. Saved docs carry this; legacy docs carry inline `data` instead.
export const BIO_GZ_FIELD = 'biomoleculesGz';

export const encodeBiomoleculeData = (map: Record<string, string>): Uint8Array =>
  gzipSync(strToU8(JSON.stringify(map)));

export const decodeBiomoleculeData = (bytes: Uint8Array): Record<string, string> => {
  const parsed = JSON.parse(strFromU8(gunzipSync(bytes)));
  return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, string>) : {};
};

/**
 * Compress the no-url structures' PDB text into a single Bytes value, trimming
 * only if the gzip'd payload still exceeds the byte budget left after the scene
 * + chat. Trimming is a rare backstop: structures are dropped oldest-first (their
 * coordinates won't survive the round-trip — the card reloads empty), keeping at
 * least the newest. Returns the Bytes to write (null when there is nothing to
 * compress) plus a human-readable note when anything was dropped.
 *
 * `candidates` are the no-url entries WITH data, in gallery (oldest-first) order.
 */
export const buildBiomoleculeField = (
  candidates: { name: string; data: string }[],
  budgetBytes: number,
): { value: Bytes | null; note: string | null } => {
  if (candidates.length === 0) return { value: null, note: null };

  const mapOf = (arr: { name: string; data: string }[]): Record<string, string> => {
    const m: Record<string, string> = {};
    for (const c of arr) m[c.name] = c.data;
    return m;
  };

  const working = candidates.slice();
  let bytes = encodeBiomoleculeData(mapOf(working));
  let dropped = 0;
  while (bytes.length > budgetBytes && working.length > 1) {
    working.shift(); // drop the oldest structure's coordinates
    dropped++;
    bytes = encodeBiomoleculeData(mapOf(working));
  }

  const note = dropped
    ? `${dropped} structure${dropped > 1 ? 's' : ''} could not be saved in full (too large) and ${dropped > 1 ? 'were' : 'was'} dropped from the saved copy.`
    : null;
  return { value: Bytes.fromUint8Array(bytes), note };
};
